import React, { useState, useContext } from "react";
import { useInterviews } from "../../contexts/InterviewsContext";
import { useMyInterviews } from "../../contexts/MyInterviewsContext";
import { AuthContext } from "../../contexts/AuthContext";
import ReusableTable from "../../components/UI/ReusableTable";
import Drawer from "../../components/UI/Drawer";
import CandidateDetails from "../../components/Details/CandidateDetails";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import StatusBadge from '../../components/UI/StatusBadge';
import Modal from '../../components/UI/Modal';
import InterviewSelectForm from '../../components/Form/InterviewSelectForm';
import InterviewRejectForm from '../../components/Form/InterviewRejectForm';

const MyScheduledInterviews = () => {
    const { updateInterviewStatus } = useInterviews();
    const { interviews, fetchMyInterviews, updateMyInterviewStatus, submitInterviewFeedback, pagination, loading } = useMyInterviews();
    const { user } = useContext(AuthContext);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedInterview, setSelectedInterview] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [notification, setNotification] = useState(null);
    const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [selectedInterviewRound, setSelectedInterviewRound] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const openDetailsDrawer = (interview) => {
        setSelectedInterview(interview); // Set the full interview object
        setIsDrawerOpen(true);
    };

    const closeDetailsDrawer = () => {
        setIsDrawerOpen(false);
        setSelectedInterview(null);
    };

    const handleStatusUpdate = async (feedback) => {
        // console.log("feedback", feedback)
        if (!selectedInterview) return;
        
        // console.log("Updating status for:", selectedInterview.id, "with:", feedback);
    
        try {
            await updateInterviewStatus(selectedInterview.id, feedback);
            setNotification({ message: "Interview status update successfully.", type: "success" });
            closeDetailsDrawer();
        } catch (error) {
            // console.error("Error updating interview status:", error);
            setNotification({ message: `Error updating interview status: ${error.message || "Unknown error"}`, type: "error" });
        }
    };

    // Helper function to get the current round for an interview
    const getCurrentRound = (interview) => {
        if (!interview?.rounds || !interview.current_round) {
            return null;
        }
        return interview.rounds.find(
            (round) => round.round_number === interview.current_round
        ) || null;
    };

    // Check if logged-in user is assigned to the current round
    const isUserAssignedToRound = (interview) => {
        if (!user?.id || !interview) return false;
        const currentRound = getCurrentRound(interview);
        return currentRound?.assigned_to?.id === user.id;
    };

    // Handle Select button click
    const handleSelectClick = (interview) => {
        const currentRound = getCurrentRound(interview);
        if (!currentRound) {
            setNotification({ message: "Error: Could not find current round for this interview.", type: "error" });
            return;
        }
        setSelectedInterviewRound({
            interview,
            round: currentRound,
        });
        setIsSelectModalOpen(true);
    };

    // Handle Reject button click
    const handleRejectClick = (interview) => {
        const currentRound = getCurrentRound(interview);
        if (!currentRound) {
            setNotification({ message: "Error: Could not find current round for this interview.", type: "error" });
            return;
        }
        setSelectedInterviewRound({
            interview,
            round: currentRound,
        });
        setIsRejectModalOpen(true);
    };

    // Handle Select form submission
    const handleSelectSubmit = async (formData) => {
        if (!selectedInterviewRound) return;

        setIsSubmitting(true);
        try {
            const roundId = selectedInterviewRound.round.id;
            // roundId goes in URL path, payload contains only form data
            const payload = { ...formData };

            await submitInterviewFeedback(roundId, payload);
            setNotification({ message: "Candidate selected successfully!", type: "success" });
            setIsSelectModalOpen(false);
            setSelectedInterviewRound(null);
            await fetchMyInterviews(currentPage);
        } catch (error) {
            console.error("Error submitting selection:", error);
            setNotification({
                message: `Error submitting selection: ${error.response?.data?.message || error.message || "Unknown error"}`,
                type: "error",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle Reject form submission
    const handleRejectSubmit = async (formData) => {
        if (!selectedInterviewRound) return;

        setIsSubmitting(true);
        try {
            const roundId = selectedInterviewRound.round.id;
            // roundId goes in URL path, payload contains only form data
            const payload = { ...formData };

            await submitInterviewFeedback(roundId, payload);
            setNotification({ message: "Candidate rejected successfully!", type: "success" });
            setIsRejectModalOpen(false);
            setSelectedInterviewRound(null);
            await fetchMyInterviews(currentPage);
        } catch (error) {
            console.error("Error submitting rejection:", error);
            setNotification({
                message: `Error submitting rejection: ${error.response?.data?.message || error.message || "Unknown error"}`,
                type: "error",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Close modals
    const closeSelectModal = () => {
        setIsSelectModalOpen(false);
        setSelectedInterviewRound(null);
    };

    const closeRejectModal = () => {
        setIsRejectModalOpen(false);
        setSelectedInterviewRound(null);
    };    

    const getColumns = (currentPage, pageSize) => {
        const columns = [
            { 
                label: "Sr. No", 
                field: "sr_no",
                render: (_row, idx) => (currentPage - 1) * pageSize + idx + 1,
            },
            {
                label: "Candidate Name",
                field: "candidate_name",
                render: (row) => (
                    <span className="text-gray-900 dark:text-white">
                        {row.candidate?.name || "Unknown Candidate"}
                    </span>
                ),
            },
            {
                label: "Round Number",
                field: "round_number",
                render: (row) => {
                    const currentRound = getCurrentRound(row);
                    if (currentRound) {
                        return (
                            <span className="text-gray-900 dark:text-white">
                                Round {currentRound.round_number}
                            </span>
                        );
                    }
                    return (
                        <span className="text-gray-500 dark:text-gray-400">
                            Round {row.current_round || "N/A"}
                        </span>
                    );
                },
            },
            {
                label: "Scheduled At",
                field: "scheduled_at",
                render: (row) =>
                    new Date(row.scheduled_at).toLocaleString("en-US", {
                        dateStyle: "medium",
                        timeStyle: "short",
                    }),
            },
            { 
                label: "Round Status", 
                field: "round_status",
                render: (row) => {
                    const currentRound = getCurrentRound(row);
                    const roundStatus = currentRound?.status || "N/A";
                    return (
                        <StatusBadge status={roundStatus} />
                    );
                },
            },
            {
                label: "Actions",
                field: "actions",
                render: (row) => {
                    const currentRound = getCurrentRound(row);
                    const roundStatus = currentRound?.status;
                    const userAssigned = isUserAssignedToRound(row);
                    const showSelectReject = roundStatus === "Scheduled" && userAssigned;
                    
                    return (
                        <div className="flex items-center gap-2 justify-center">
                            <button
                                onClick={() => openDetailsDrawer(row)}
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                View Details
                            </button>
                            {showSelectReject && (
                                <>
                                    <button
                                        onClick={() => handleSelectClick(row)}
                                        className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                                    >
                                        Select
                                    </button>
                                    <button
                                        onClick={() => handleRejectClick(row)}
                                        className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                                    >
                                        Reject
                                    </button>
                                </>
                            )}
                        </div>
                    );
                },
            },
        ];
        return columns;
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
          setCurrentPage(newPage);
          fetchMyInterviews(newPage);
        }
    };
      
    return (
        <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
            
            {/* notification compo */}
            {notification && (
                <Notification 
                    message={typeof notification === 'string' ? notification : notification.message}
                    type={typeof notification === 'string' ? 'info' : (notification.type || 'info')}
                    onClose={() => setNotification(null)} 
                />
            )}

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
                    My Scheduled Interviews
                </h2>
            </div>

            {/* Interviews Table or Empty State */}
            <div>
                <ReusableTable
                    data={interviews}
                    // columns={columns}
                    loading={loading}
                    filterable={false}
                    columns={getColumns(currentPage, pagination.perPage)}
                />

                {interviews?.length > 0 && (
                <ReactPaginate
                    breakLabel="..."
                    nextLabel="Next >"
                    onPageChange={(event) => handlePageChange(event.selected + 1)}
                    forcePage={currentPage - 1}
                    pageRangeDisplayed={5}
                    disabledClassName='opacity-50 cursor-not-allowed'
                    disabledLinkClassName='pointer-events-none'
                    pageCount={pagination.totalPages}
                    previousLabel="< Prev"
                    previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
                    previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
                    nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
                    nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    renderOnZeroPageCount={null}
                    containerClassName="flex items-center justify-center mt-4 font-poppins"
                    pageClassName="mx-1 border rounded dark:text-white font-poppins"
                    pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
                    activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
                    activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
                  />
                )}
            </div>

            {/* Drawer for Candidate Details */}
            <Drawer
                isOpen={isDrawerOpen}
                onClose={closeDetailsDrawer}
                ChilDiv=""
                ChildParent=""
                showClose = {false}
                border_radius="md:rounded-none"
            >
                {selectedInterview ? (
                    <CandidateDetails
                        candidate={selectedInterview.candidate} // Pass candidate details
                        interview={selectedInterview} // Pass the full interview object
                        onUpdateStatus={handleStatusUpdate}
                        onClose={closeDetailsDrawer}
                        onSelectClick={handleSelectClick}
                        onRejectClick={handleRejectClick}
                    />
                ) : (
                    <p className="text-center text-gray-500">
                        
                    </p>
                )}
            </Drawer>

            {/* Select Modal */}
            <Modal
                isOpen={isSelectModalOpen}
                onRequestClose={closeSelectModal}
                title="Select Candidate"
                fullSize={false}
            >
                <InterviewSelectForm
                    onSubmit={handleSelectSubmit}
                    onCancel={closeSelectModal}
                    loading={isSubmitting}
                />
            </Modal>

            {/* Reject Modal */}
            <Modal
                isOpen={isRejectModalOpen}
                onRequestClose={closeRejectModal}
                title="Reject Candidate"
                fullSize={false}
            >
                <InterviewRejectForm
                    onSubmit={handleRejectSubmit}
                    onCancel={closeRejectModal}
                    loading={isSubmitting}
                />
            </Modal>
        </div>
    );
};

export default MyScheduledInterviews;
